import { Intro, DataTable, PopOverMenu, Radio } from 'components/commons';
import { useApi, useFilter, useMount } from 'hooks';
import useModal from 'hooks/useModal';
import React, { useCallback, useMemo, useState } from 'react';
import { accountColumns } from './account-columns';
import AccountFilter from './account-filter';
import { accountFilterState } from './account-filter.state';
import DriverForm from './account-form';
import styles from './account.module.scss';
import locale from 'localization';
import { getAccounts } from 'apis/account.api';
import { accountCoaColumns } from './account-coa-columns';

const AccountModule = () => {
  const [viewMode, setViewMode] = useState('table-view');

  const { modifyFilter, modifyFilters, filterState, requestState, submitFilter } = useFilter(accountFilterState());
  const accountFormModal = useModal();

  const {
    request: getAccountsRequest,
    loading: fetchingAccounts,
    result: getAccountsResult = { accounts: [], count: 0 }
  } = useApi({
    api: getAccounts,
    pageError: true
  });

  useMount(() => {
    fetchAccounts(requestState);
  });

  const fetchAccounts = useCallback(
    (rs) => {
      getAccountsRequest(submitFilter({ ...rs }));
    },
    [getAccountsRequest, submitFilter]
  );

  const preparedAccountsData = useMemo(() => {
    const { accounts } = getAccountsResult;
    if (accounts.length > 0) {
      if (viewMode === 'level-view') {
        return accounts.map((account) => {
          const row = new Map();

          // Level 1: Account Category
          row.set('codeLevel1', account.accountCategory?.code || '');
          row.set('titleLevel1', account.accountCategory?.name || '');

          // Level 2–6: Walk up parentAccount chain
          const levels = [];
          let current = account;
          for (let i = 0; i < 6 && current; i++) {
            levels.unshift({
              code: current.code,
              title: current.name
            });
            current = current.parentAccount;
          }

          levels.forEach((lvl, idx) => {
            const levelIndex = idx + 2; // Start from Level 2
            row.set(`codeLevel${levelIndex}`, lvl.code || '');
            row.set(`titleLevel${levelIndex}`, lvl.title || '');
          });

          return row;
        });
      }

      const preparedData = accounts.map((d) => {
        const map = new Map();
        map.set('name', d.name);
        map.set('type', d.type);
        map.set('financialStatement', d.financialStatement);
        map.set('code', d.code);
        map.set('category', d?.accountCategory?.name || '--');
        map.set('isPostable', d?.isPostable ? 'Yes' : 'No');
        map.set('isAffectsCashflow', d?.isAffectsCashflow ? 'Yes' : 'No');
        map.set('isCash', d?.isCash ? 'Yes' : 'No');

        let options = [];
        let editDetails;

        editDetails = {
          content: locale.editDetails,
          onClick: () => {
            accountFormModal.show({
              title: locale.editDetails,
              actionText: locale.save,
              create: false,
              accountId: d.accountId,
              parentAccountId: d.parentAccountId,
              account: d.account,
              initialState: {
                code: {
                  value: d.code
                },
                name: {
                  value: d.name
                },
                financialStatement: {
                  value: d.financialStatement
                },
                accountCategoryId: {
                  label: d.accountCategory?.name,
                  value: d.accountCategoryId
                },
                parentAccountId: {
                  label: d.parentAccount?.name,
                  value: d.parentAccountId
                },
                level: {
                  label: d.level,
                  value: d.level.toString()
                },
                isPostable: {
                  value: d.isPostable
                },
                isAffectsCashflow: {
                  value: d.isAffectsCashflow
                },
                isCash: {
                  value: d.isCash
                }
              }
            });
          }
        };
        options.push(editDetails);

        map.set('options', <PopOverMenu options={options} />);
        return map;
      });
      return preparedData;
    }
    return [];
  }, [getAccountsResult, viewMode, accountFormModal]);

  const onSearchCb = useCallback(
    (searchKey) => {
      let { requestState } = modifyFilters({ searchKey, page: 1 });

      if (viewMode === 'level-view') {
        fetchAccounts({ searchKey, page: null, perPage: null });
        return;
      }

      fetchAccounts(requestState);
    },
    [fetchAccounts, modifyFilters, viewMode]
  );

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchAccounts(requestState);
    },
    [fetchAccounts, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchAccounts(requestState);
    },
    [fetchAccounts, modifyFilters]
  );

  return (
    <div>
      <DriverForm
        {...accountFormModal}
        refreshPage={() => {
          getAccountsRequest(requestState);
        }}
      />
      <div>
        <Intro
          title={'Manage chart of accounts'}
          subtitle={locale.manageYourChartOfAccounts}
          actionText={localStorage.getItem('role') === 'admin' ? locale.addAccount : null}
          actionOnClick={() => {
            accountFormModal.show({
              title: locale.addAccount,
              actionText: locale.add,
              create: true,
              initialState: {
                code: {
                  value: ''
                },
                name: {
                  value: ''
                },
                type: {
                  value: ''
                },
                accountCategoryId: {
                  label: '',
                  value: ''
                },
                financialStatement: {
                  value: ''
                }
              }
            });
          }}
        />
      </div>
      <div className={styles.filters}>
        <div>
          <AccountFilter filterState={filterState} onSearchChange={modifyFilter} onSearch={onSearchCb} />
        </div>
        <div className={styles.viewMode}>
          <Radio
            className={styles.radioContainer}
            radioClassName={styles.radio}
            options={[
              { text: 'Table View', value: 'table-view' },
              { text: 'Level View', value: 'level-view' }
            ]}
            value={viewMode}
            onChange={(name, { value }) => {
              setViewMode(value);

              if (value === 'level-view') {
                const { requestState } = modifyFilters({ page: null, perPage: null });
                fetchAccounts(requestState);
              } else {
                const { requestState } = modifyFilters({ page: 1 });
                fetchAccounts(requestState);
              }
            }}
          />
        </div>
      </div>
      <div className={styles.table}>
        <DataTable
          loading={fetchingAccounts}
          page={filterState.page}
          pageSize={filterState.perPage}
          columns={viewMode === 'table-view' ? accountColumns : accountCoaColumns}
          data={preparedAccountsData}
          dataCount={getAccountsResult.count}
          onChangePage={onChangePageCb}
          onChangePageSize={onChangePageSizeCb}
          hidePagination={viewMode === 'level-view'}
        />
      </div>
    </div>
  );
};

export default AccountModule;
